import React, { useState } from "react";
import { toast } from "sonner";
import { adminCreateCourse, adminUpdateCourse, adminDeleteCourse } from "@/lib/api";
import Bloques from "./Bloques";
import PortadaUploader from "./PortadaUploader";
import { GripVertical, X, ChevronDown, ChevronRight, Plus, Save, ArrowLeft, Trash2 } from "lucide-react";

const area = { width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "10px 12px", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, borderRadius: 8 };
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const move = (list, from, to) => { const a = [...list]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; };

// Convierte lecciones legacy en capítulos con bloques
const legacyToBloques = (l) => {
  const b = [];
  if (l.contenido) b.push({ tipo: "texto", texto: l.contenido });
  (l.videos || []).forEach((v) => {
    if (v.tipo === "upload" && v.file_url) b.push({ tipo: "video", file_url: v.file_url });
    else if (v.url) b.push({ tipo: v.tipo === "vimeo" ? "vimeo" : "youtube", url: v.url });
  });
  if (l.video_tipo && !(l.videos || []).length) {
    if (l.video_tipo === "upload" && l.video_file_url) b.push({ tipo: "video", file_url: l.video_file_url });
    else if (l.video_url) b.push({ tipo: l.video_tipo === "vimeo" ? "vimeo" : "youtube", url: l.video_url });
  }
  (l.imagenes || []).forEach((img) => b.push({ tipo: "imagen", file_url: img }));
  (l.recursos || []).forEach((r) => b.push({ tipo: r.tipo === "imagen" ? "imagen" : "pdf", file_url: r.url, titulo: r.nombre }));
  return b;
};

const normalize = (curso) => {
  if (!curso) return { slug: "", titulo: "", descripcion: "", categoria: "Bonus", horas: 4, orden: 10, precio_mensual: 0, acceso: "all", destacado: false, modulos: [] };
  const modulos = (curso.modulos || []).map((m) => {
    let submodulos = (m.submodulos || []).map((s) => ({
      id: s.id || uid(), titulo: s.titulo || "", descripcion: s.descripcion || "", bloques: s.bloques || [],
      capitulos: (s.capitulos || []).map((c) => ({ id: c.id || uid(), titulo: c.titulo || "", descripcion: c.descripcion || "", bloques: c.bloques || [] })),
    }));
    // legacy: lecciones -> un submódulo "General" con capítulos
    if ((m.lecciones || []).length && submodulos.length === 0) {
      const bySub = {};
      const order = [];
      (m.lecciones || []).forEach((l) => {
        const key = l.submodulo || "General";
        if (!bySub[key]) { bySub[key] = []; order.push(key); }
        bySub[key].push({ id: uid(), titulo: l.titulo || "", bloques: legacyToBloques(l) });
      });
      submodulos = order.map((k) => ({ id: uid(), titulo: k, bloques: [], capitulos: bySub[k] }));
    }
    return { titulo: m.titulo || "", descripcion: m.descripcion || "", portada: m.portada || "", bloques: m.bloques || [], submodulos };
  });
  return { ...curso, modulos };
};

function Section({ title, tone, children, right, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: `1px solid ${tone || "var(--line)"}`, borderRadius: 10, marginBottom: 10, background: "rgba(255,255,255,.02)" }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px" }}>
        <button type="button" className="abtn" style={{ padding: "2px 6px" }} onClick={() => setOpen((o) => !o)}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</button>
        {title}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>{right}</div>
      </div>
      {open && <div style={{ padding: "0 14px 14px" }}>{children}</div>}
    </div>
  );
}

export default function CourseEditor({ curso, onClose, onSaved }) {
  const creating = !curso;
  const [f, setF] = useState(() => normalize(curso));
  const [dragMod, setDragMod] = useState(null);
  const isMaster = f.slug === "master";

  const setF2 = (fn) => setF((s) => fn(s));
  const set = (k, v) => setF2((s) => ({ ...s, [k]: v }));
  const setModulos = (fn) => setF2((s) => ({ ...s, modulos: fn(s.modulos) }));

  // ---- módulos ----
  const addMod = () => setModulos((ms) => [...ms, { titulo: "", descripcion: "", portada: "", bloques: [], submodulos: [] }]);
  const updMod = (mi, patch) => setModulos((ms) => ms.map((m, j) => (j === mi ? { ...m, ...patch } : m)));
  const delMod = (mi) => { if (!window.confirm("¿Eliminar este módulo y todo su contenido?")) return; setModulos((ms) => ms.filter((_, j) => j !== mi)); };

  // ---- submódulos ----
  const addSub = (mi) => setModulos((ms) => ms.map((m, j) => (j === mi ? { ...m, submodulos: [...m.submodulos, { id: uid(), titulo: "", descripcion: "", bloques: [], capitulos: [] }] } : m)));
  const updSub = (mi, si, patch) => setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: m.submodulos.map((s, k) => (k === si ? { ...s, ...patch } : s)) })));
  const delSub = (mi, si) => { if (!window.confirm("¿Eliminar este submódulo y sus capítulos?")) return; setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: m.submodulos.filter((_, k) => k !== si) }))); };
  const moveSub = (mi, from, to) => { if (from === null || from === to) return; setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: move(m.submodulos, from, to) }))); };

  // ---- capítulos ----
  const addCap = (mi, si) => setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: m.submodulos.map((s, k) => (k !== si ? s : { ...s, capitulos: [...s.capitulos, { id: uid(), titulo: "", descripcion: "", bloques: [] }] })) })));
  const updCap = (mi, si, ci, patch) => setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: m.submodulos.map((s, k) => (k !== si ? s : { ...s, capitulos: s.capitulos.map((c, x) => (x === ci ? { ...c, ...patch } : c)) })) })));
  const delCap = (mi, si, ci) => { if (!window.confirm("¿Eliminar este capítulo?")) return; setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: m.submodulos.map((s, k) => (k !== si ? s : { ...s, capitulos: s.capitulos.filter((_, x) => x !== ci) })) }))); };
  const moveCap = (mi, si, from, to) => { if (from === null || from === to) return; setModulos((ms) => ms.map((m, j) => (j !== mi ? m : { ...m, submodulos: m.submodulos.map((s, k) => (k !== si ? s : { ...s, capitulos: move(s.capitulos, from, to) })) }))); };

  const save = async () => {
    try {
      const modulos = f.modulos.map((m, mi) => ({
        orden: mi + 1, titulo: m.titulo || `Módulo ${mi + 1}`, descripcion: m.descripcion || "", portada: m.portada || "", bloques: m.bloques || [],
        submodulos: (m.submodulos || []).map((s, si) => ({
          id: s.id || uid(), orden: si + 1, titulo: s.titulo || `Submódulo ${si + 1}`, descripcion: s.descripcion || "", bloques: s.bloques || [],
          capitulos: (s.capitulos || []).map((c, ci) => ({ id: c.id || uid(), orden: ci + 1, titulo: c.titulo || `Capítulo ${ci + 1}`, descripcion: c.descripcion || "", bloques: c.bloques || [] })),
        })),
        lecciones: [],
      }));
      const totalCaps = modulos.reduce((a, m) => a + m.submodulos.reduce((b, s) => b + s.capitulos.length, 0), 0);
      const slug = creating ? (f.slug || f.titulo).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") : f.slug;
      const payload = { ...f, slug, modulos, lecciones: totalCaps, horas: +f.horas || 0, orden: +f.orden || 10, precio_mensual: +f.precio_mensual || 0, acceso: f.acceso || "all" };
      if (creating) await adminCreateCourse(payload); else await adminUpdateCourse(curso.slug, payload);
      toast.success("Curso guardado ✓"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  const borrarCurso = async () => {
    if (isMaster) { toast.error("El Máster no se puede eliminar (la web de ventas depende de él)."); return; }
    if (!window.confirm("¿Eliminar este curso por completo? No se puede deshacer.")) return;
    try { await adminDeleteCourse(curso.slug); toast.success("Curso eliminado"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div data-testid="course-editor" style={{ position: "fixed", inset: 0, zIndex: 500, background: "var(--ink)", overflowY: "auto" }}>
      {/* barra superior fija */}
      <div style={{ position: "sticky", top: 0, zIndex: 5, background: "var(--ink2, #121214)", borderBottom: "1px solid var(--line)", padding: "12px 20px", display: "flex", alignItems: "center", gap: 12 }}>
        <button className="abtn" onClick={onClose} data-testid="editor-cerrar"><ArrowLeft size={15} /> Cerrar</button>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{creating ? "Nuevo curso" : "Editando"} · <b style={{ color: "var(--gold)" }}>{f.titulo || "sin título"}</b></div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {!creating && !isMaster && <button className="abtn danger" onClick={borrarCurso} data-testid="editor-borrar-curso"><Trash2 size={14} /> Eliminar curso</button>}
          <button className="btn btn-gold btn-sm" onClick={save} data-testid="editor-guardar"><Save size={14} /> Guardar</button>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "22px 20px 80px" }}>
        <h2 className="serif" style={{ fontSize: 24, marginBottom: 16 }}>{creating ? "Crear curso" : "Editor del curso"}</h2>

        {/* datos del curso */}
        <div style={{ display: "grid", gap: 12, marginBottom: 24 }}>
          <div className="field" style={{ margin: 0 }}><label>Título del curso</label>
            <input value={f.titulo} onChange={(e) => set("titulo", e.target.value)} style={area} data-testid="curso-titulo" /></div>
          <div className="field" style={{ margin: 0 }}><label>Descripción</label>
            <textarea value={f.descripcion || ""} onChange={(e) => set("descripcion", e.target.value)} style={{ ...area, minHeight: 64 }} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
            <div className="field" style={{ margin: 0 }}><label>Categoría</label><input value={f.categoria || ""} onChange={(e) => set("categoria", e.target.value)} style={area} /></div>
            <div className="field" style={{ margin: 0 }}><label>¿Quién lo ve?</label>
              <select value={f.acceso || "all"} onChange={(e) => set("acceso", e.target.value)} style={area} data-testid="curso-acceso">
                <option value="all">Todos</option><option value="enrolled">Solo inscritos</option><option value="vip">Solo VIP</option>
              </select></div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13.5 }}>
            <input type="checkbox" checked={!!f.destacado} onChange={(e) => set("destacado", e.target.checked)} data-testid="curso-destacado" />
            Marcar como <b style={{ color: "var(--gold)" }}>NUEVO</b> (avisa a los alumnos al crearlo)
          </label>
          <PortadaUploader value={f.portada} onChange={(v) => set("portada", v)} testid="curso-portada" label="Foto de portada del curso" />
        </div>

        {/* estructura */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <label style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)" }}>Contenido · Módulos › Submódulos › Capítulos</label>
        </div>

        {f.modulos.map((m, mi) => (
          <div key={mi} draggable data-testid={`modulo-${mi}`}
            onDragStart={() => setDragMod(mi)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragMod !== null && dragMod !== mi) setModulos((ms) => move(ms, dragMod, mi)); setDragMod(null); }}
            style={{ marginBottom: 14, border: "1px solid var(--gold)", borderRadius: 12, background: dragMod === mi ? "rgba(47,107,255,.10)" : "rgba(197,164,90,.05)", padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ cursor: "grab", color: "var(--gold)" }} data-testid={`modulo-drag-${mi}`}><GripVertical size={16} /></span>
              <span style={{ color: "var(--gold)", fontWeight: 700, fontSize: 13 }}>M{mi + 1}</span>
              <input value={m.titulo} onChange={(e) => updMod(mi, { titulo: e.target.value })} placeholder={`Título del módulo ${mi + 1}`} style={{ ...area, flex: 1, fontWeight: 600 }} data-testid={`modulo-titulo-${mi}`} />
              <button className="abtn danger" onClick={() => delMod(mi)} data-testid={`modulo-del-${mi}`}><X size={14} /></button>
            </div>
            <div className="field" style={{ margin: "0 0 10px" }}><label>Descripción del módulo</label>
              <input value={m.descripcion || ""} onChange={(e) => updMod(mi, { descripcion: e.target.value })} style={area} /></div>

            <div style={{ marginBottom: 12 }}>
              <PortadaUploader value={m.portada} onChange={(v) => updMod(mi, { portada: v })} testid={`modulo-portada-${mi}`} label="Foto de portada del módulo" />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 6 }}>Contenido del módulo (opcional)</label>
              <Bloques value={m.bloques} onChange={(list) => updMod(mi, { bloques: list })} tid={`m${mi}`} />
            </div>

            {/* submódulos */}
            {m.submodulos.map((s, si) => (
              <SubmoduloEditor key={s.id || si} m={m} s={s} mi={mi} si={si}
                updSub={updSub} delSub={delSub} moveSub={moveSub}
                addCap={addCap} updCap={updCap} delCap={delCap} moveCap={moveCap} />
            ))}
            <button className="abtn" onClick={() => addSub(mi)} data-testid={`add-submodulo-${mi}`}><Plus size={13} /> Añadir submódulo</button>
          </div>
        ))}
        <button className="btn btn-gold btn-sm" onClick={addMod} data-testid="add-modulo"><Plus size={14} /> Añadir módulo</button>
      </div>
    </div>
  );
}

function SubmoduloEditor({ m, s, mi, si, updSub, delSub, moveSub, addCap, updCap, delCap, moveCap }) {
  const [dragCap, setDragCap] = useState(null);
  return (
    <div draggable data-testid={`submodulo-${mi}-${si}`}
      onDragStart={(e) => { e.stopPropagation(); }} onDragOver={(e) => e.preventDefault()}
      style={{ margin: "10px 0", border: "1px solid var(--line)", borderRadius: 10, background: "rgba(47,107,255,.04)", padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ color: "var(--muted)" }}>›</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#5B8CFF" }}>Submódulo {si + 1}</span>
        <input value={s.titulo} onChange={(e) => updSub(mi, si, { titulo: e.target.value })} placeholder="Título del submódulo" style={{ ...area, flex: 1, fontWeight: 600 }} data-testid={`submodulo-titulo-${mi}-${si}`} />
        <button className="abtn" disabled={si === 0} onClick={() => moveSub(mi, si, si - 1)} title="Subir">▲</button>
        <button className="abtn danger" onClick={() => delSub(mi, si)} data-testid={`submodulo-del-${mi}-${si}`}><X size={13} /></button>
      </div>
      <div className="field" style={{ margin: "0 0 10px" }}><label>Descripción del submódulo (opcional)</label>
        <textarea value={s.descripcion || ""} onChange={(e) => updSub(mi, si, { descripcion: e.target.value })} style={{ ...area, minHeight: 54 }} data-testid={`submodulo-desc-${mi}-${si}`} /></div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 6 }}>Contenido del submódulo (opcional)</label>
        <Bloques value={s.bloques} onChange={(list) => updSub(mi, si, { bloques: list })} tid={`s${mi}-${si}`} />
      </div>
      {/* capítulos */}
      {s.capitulos.map((c, ci) => (
        <div key={c.id || ci} draggable data-testid={`capitulo-${mi}-${si}-${ci}`}
          onDragStart={(e) => { e.stopPropagation(); setDragCap(ci); }} onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.stopPropagation(); if (dragCap !== null && dragCap !== ci) moveCap(mi, si, dragCap, ci); setDragCap(null); }}
          style={{ margin: "8px 0", border: "1px solid var(--line)", borderRadius: 8, background: dragCap === ci ? "rgba(47,107,255,.10)" : "rgba(255,255,255,.02)", padding: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ cursor: "grab", color: "var(--muted)" }} data-testid={`capitulo-drag-${mi}-${si}-${ci}`}><GripVertical size={14} /></span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--muted)" }}>Cap. {ci + 1}</span>
            <input value={c.titulo} onChange={(e) => updCap(mi, si, ci, { titulo: e.target.value })} placeholder="Título del capítulo (clase)" style={{ ...area, flex: 1 }} data-testid={`capitulo-titulo-${mi}-${si}-${ci}`} />
            <button className="abtn danger" onClick={() => delCap(mi, si, ci)} data-testid={`capitulo-del-${mi}-${si}-${ci}`}><X size={13} /></button>
          </div>
          <div className="field" style={{ margin: "0 0 8px" }}><label style={{ fontSize: 11, color: "var(--muted)" }}>Descripción del capítulo (opcional)</label>
            <textarea value={c.descripcion || ""} onChange={(e) => updCap(mi, si, ci, { descripcion: e.target.value })} style={{ ...area, minHeight: 50 }} data-testid={`capitulo-desc-${mi}-${si}-${ci}`} /></div>
          <Bloques value={c.bloques} onChange={(list) => updCap(mi, si, ci, { bloques: list })} tid={`c${mi}-${si}-${ci}`} />
        </div>
      ))}
      <button className="abtn" onClick={() => addCap(mi, si)} data-testid={`add-capitulo-${mi}-${si}`}><Plus size={13} /> Añadir capítulo</button>
    </div>
  );
}
